// 🧊🗂 «모션 무대 가족» 표 — 설계도 가족 중 «장표 씬을 자기 무대(캔버스)로 덮어 그리는» 스타일의 꽂는 자리 하나(2026-10-02 · 2-0).
//   정본 지도 `docs/01-plan/features/motion-style-pack.md` §12-12.
//
// ★왜 표인가★: 모션 브리핑(`blueprintMotion`) 하나일 때는 «스킨 이름이 이거면 다르게 굴어라» 분기가 렌더·정밀편집·채팅·직접 수정에
//   이름으로 박혀 있었다. 스타일이 늘면 그 분기가 스타일 수만큼 갈린다 → 이름 비교를 전부 이 표 조회로 바꿨다(«얹을 때 걷기»).
//   `tests/unit/motion-stage-family.test.ts` 가 «src 에 스킨 이름 비교가 다시 생기면 red» 로 잠근다.
//
// ★표에 오른다는 것의 뜻(가족 공통 · 행마다 다르지 않다)★
//   ① 장표가 있는 씬은 무대 캔버스가 화면을 덮는다 → 그 씬의 그림은 보이지 않는다(`sceneImageCoveredBySlide`).
//   ② 모션 부품(`motionKit`)이 테넌트 스위치와 무관하게 기본으로 켜진다.
//   ③ 장표도 그림도 없는 씬은 그 씬 내레이션으로 모션 타이포를 세워 메운다(`briefFillerFrom`).
//   ④ 일반 설계도 장표의 딤·돌진·쓸림·속도선·모서리 접힘은 쓰지 않는다(무대가 전환을 맡는다).
//
// ★새 스타일을 붙이는 법(이 파일 1행 + 무대 맵 1행 + 등록 줄)★
//   1. 여기 `MOTION_STAGE_FAMILY` 에 1행(키 = `overlaySkin` id).
//   2. `src/remotion/msb/motion-stages.tsx` `MOTION_STAGES` 에 무대 종류 1행(`{ Stage, Exit }`).
//   3. 스타일 등록 줄(스타일마다 1행인 자리 — 목록은 위 테스트의 `REGISTRY_FILES`).
//   `Overlay169Template.tsx`·`Overlay169SlideLayer.tsx`·`precision-source-slides.ts`·채팅·직접 수정은 다시 고치지 않는다.
//
// 순수 데이터 — remotion·React 를 import 하지 않는다(채팅·직접 수정·정밀편집이 같이 읽는다).

/** 무대 종류 — 무대 컴포넌트 맵(`MOTION_STAGES`)의 열쇠. */
export type MotionStageKind = "brief" | "showreel" | "colorcut";

export interface MotionStageDef {
  /** 표의 키 = `overlaySkin` id. 정밀편집 프로토콜 `context.motionStage` 에도 이 값이 실린다(≤64자). */
  skin: string;
  stage: MotionStageKind;
  /** 자막 띠 바탕색 — 저장된 자막 글자색이 이 색과 대비 4.5:1 미만이면 스킨 글자색으로 바꾼다(다른 스타일에서 옮겨 온 작업물). */
  captionBand: string;
  /** 채팅 결정론 경로(장면 추가·삭제·순서 · 장표 종류 · 브랜드색)를 켠다 — EF 에 가기 전에 직접 수정과 같은 함수를 부른다. */
  deterministicChat: boolean;
  /** 무대가 칸 계획(`briefCellPlan`)으로 «한 줄 카드 수»를 다시 정한다 → 직접 수정 «연출» 조작부가 3장이 드는지 미리 계산한다. */
  cellPlanCols: boolean;
}

export const MOTION_STAGE_FAMILY: Readonly<Record<string, MotionStageDef>> = {
  // 🧊 모션 브리핑 — 밝은 캔버스 + 변신하는 도형 하나(§12-5~12-11).
  blueprintMotion: { skin: "blueprintMotion", stage: "brief", captionBand: "#FFFFFF", deterministicChat: true, cellPlanCols: true },
  // 🎬 쇼릴 브리핑(가칭) — 어두운 바탕 + 원색 판 + 띠 쓸림 컷 + 3D 구슬(§12-13). 글자 자리 계산은 모션 브리핑과 같은 함수.
  blueprintShowreel: { skin: "blueprintShowreel", stage: "showreel", captionBand: "#111827", deterministicChat: true, cellPlanCols: true },
  // 🎨 컬러컷 — 화면 전체가 원색 한 장 · 장면마다 다음 색이 아래→위로 쓸려 올라옴 · 큰 글자가 한 자씩(정본 지도 §12-14). 자막 띠는 먹.
  blueprintColorCut: { skin: "blueprintColorCut", stage: "colorcut", captionBand: "#111827", deterministicChat: true, cellPlanCols: true },
};

/** 🎨 컬러컷(`blueprintColorCut`)의 표시 이름 — ★10-05 회장님 확정 «컬러 브리핑»★(고객 공개 · 본부 착수문 §5). 이름 문자열은 여기 한 곳에만 두고 가족·테마·견본이 읽는다. */
export const COLOR_CUT_STYLE_LABEL = "컬러 브리핑";
/** 🎬 쇼릴형(`blueprintShowreel`)의 표시 이름 — ★10-05 회장님 확정 «임팩트 브리핑»★(가칭 «쇼릴 브리핑»). 컬러 브리핑과 같은 규칙: 이름은 여기 한 곳. */
export const SHOWREEL_STYLE_LABEL = "임팩트 브리핑";

/** 무대 키를 못 읽을 때의 해석 — 정밀편집 옛 페이로드(`motionBrief: true` 만 있음)·모르는 키(앱이 늦게 배포됨)는 모션 브리핑 무대로 그린다. */
export const DEFAULT_MOTION_STAGE_SKIN = "blueprintMotion";

/** 이 스킨의 모션 무대 정의 — 가족이 아니면 null(다른 스타일은 종전 그대로). */
export function motionStageOf(skin: unknown): MotionStageDef | null {
  return typeof skin === "string" && Object.prototype.hasOwnProperty.call(MOTION_STAGE_FAMILY, skin) ? MOTION_STAGE_FAMILY[skin] : null;
}

/** 정밀편집 페이로드의 무대 키 읽기(읽기 관용) — 없거나 모르는 값이면 기본 무대. */
export function motionStageFromKey(key: unknown): MotionStageDef {
  return motionStageOf(key) ?? MOTION_STAGE_FAMILY[DEFAULT_MOTION_STAGE_SKIN];
}

/** 채팅 결정론 경로를 켜는 스킨 — 표에서 파생(넓힐 때는 표의 `deterministicChat`). */
export const MOTION_STAGE_CHAT_SKINS: ReadonlySet<string> = new Set(
  Object.values(MOTION_STAGE_FAMILY).filter((d) => d.deterministicChat).map((d) => d.skin),
);
